import { Apartment, Group } from '@mui/icons-material';
import { Box, Button, Stack, styled, Typography } from '@mui/material';
import { useIsMobile } from '@side/pantry';
import type { HTMLAttributes } from 'react';
import { highlightSubstring } from '@/common/utils/string';
import type { DropdownOption } from '@/views/Community/views/CommunityListings/components/FilterButton/FilterDialog/LocationAutocomplete/LocationAutocomplete.helpers';

type Props = HTMLAttributes<HTMLLIElement> & {
  searchTerm: string;
  option: DropdownOption;
  onClickViewAll: () => void;
};

const ViewAllLocationsButton = styled(Button)(({ theme }) => ({
  padding: 0,
  lineHeight: theme.typography.body1.lineHeight,
  flexShrink: 0,
  '&:hover, &:active': {
    backgroundColor: 'transparent',
  },
}));

export function TeamOption({
  option,
  searchTerm,
  onClickViewAll,
  ...optionProps
}: Props) {
  const isMobile = useIsMobile('md');

  if (!option) {
    return null;
  }

  const { label, secondaryLabel, optionType, id, parentId } = option;
  const teamId = parentId ?? id;
  const Icon = optionType === 'parentTeam' ? Apartment : Group;

  return (
    <Stack
      component="li"
      direction="row"
      sx={[
        {
          alignItems: 'center',
          gap: 2,
          flexWrap: 'noWrap',
        },
        optionType === 'parentTeam' && {
          [`& ~ .MuiAutocomplete-option[data-team-id="${id}"]`]: {
            paddingLeft: 6,
          },
        },
      ]}
      data-team-id={teamId}
      {...optionProps}
    >
      <Icon sx={{ color: 'action.active' }} />
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            wordBreak: 'break-word',
            flexGrow: 1,
          }}
        >
          {label && (
            <Typography
              variant="body2"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: Needed for highlighting
              dangerouslySetInnerHTML={{
                __html: highlightSubstring(label, searchTerm),
              }}
            />
          )}
          {secondaryLabel && (
            <Typography
              variant="body2"
              sx={{
                pt: 0.25,
                color: 'text.secondary',
              }}
            >
              {secondaryLabel}
            </Typography>
          )}
        </Box>
        {optionType === 'parentTeam' && (
          <ViewAllLocationsButton
            size="small"
            variant="text"
            className="viewAll"
            onClick={(e) => {
              // Prevent selection of option
              e.stopPropagation();
              onClickViewAll();
            }}
          >
            {isMobile ? 'View All' : 'View All Locations'}
          </ViewAllLocationsButton>
        )}
      </Stack>
    </Stack>
  );
}
